"use client";
import { useEffect, useState } from "react";

import Link from "next/link";
import { useTranslations } from "next-intl";

import { useDispatch, useSelector } from "react-redux";

import { setSelectedPackagesId, setStepNumber, setTemplateId } from "@/store";
import { CheckIcon } from "@heroicons/react/24/solid";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

export const StepProgressBar = ({ stepNumber = "1", searchParams }) => {

  const dispatch = useDispatch();
  const t = useTranslations();

  const { template_id: templateID } = searchParams || {}

  const { template_id } = useSelector((state) => state.template);



  const [steps, setSteps] = useState([
    {
      id: "1",
      name: `${t('step')} 1`,
      description: t("selectTheme"),
      href: "?step=1",
      onClick: () => dispatch(setTemplateId(null)),
      status: "current",
    },
    {
      id: "2",
      name: `${t('step')} 2`,
      description: t("selectPackages"),
      onClick: () => dispatch(setSelectedPackagesId(null)),
      href: `?step=2&template_id=${templateID||template_id}`,
      status: "upcoming",
    },
    {
      id: "3",
      name: `${t('step')} 3`,
      description: t("payPublish"),
      href: "#",
      status: "upcoming",
    },
  ]);

  const handleChangeStep = (stepId) => {
    let updatedSteps;
    if (stepId === "1") {
      updatedSteps = steps?.map((step) => {
        if (step.id === "1") {
          return { ...step, status: "current" };
        } else {
          return { ...step, status: "upcoming" };
        }
      });
    } else if (stepId === "2") {
      updatedSteps = steps?.map((step) => {
        if (step.id === "1") {
          return { ...step, status: "complete" };
        } else if (step.id === "2") {
          return { ...step, status: "current" };
        } else {
          return { ...step, status: "upcoming" };
        }
      });
    } else if (stepId === "3") {
      updatedSteps = steps?.map((step) => {
        if (step.id === "1" || step.id === "2") {
          return { ...step, status: "complete" };
        } else if (step.id === "3") {
          return { ...step, status: "current" };
        } else {
          return { ...step, status: "upcoming" };
        }
      });
    } else {
      updatedSteps = steps;
    }
    setSteps(updatedSteps);
  };

  useEffect(() => {
    handleChangeStep(stepNumber);
    window.scrollTo({
      top: 0,
      behavior: "smooth",
    });
  }, [stepNumber]);

  return (
    <div className="lmd:mt-4 mt-2 hidden xmd:block">
      <nav
        className="mx-auto max-w-7xl px-4 sm:px-6 lmd:px-8"
        aria-label="Progress"
      >
        <ol
          role="list"
          className="overflow-hidden rounded-md lmd:flex gap-x-8 lmd:rounded-none "
        >
          {steps.map((step, stepIdx) => (
            <li key={step.id} className="relative overflow-hidden lmd:flex-1">
              <div
                onClick={() =>
                  step.id == "1" ? dispatch(setStepNumber("1")) : null
                }
                className={classNames(
                  stepIdx === 0 ? "rounded-t-md border-b-0" : "",
                  stepIdx === steps.length - 1 ? "rounded-b-md border-t-0" : "",
                  "overflow-hidden border border-gray-200 lmd:border-0"
                )}
              >
                {step.status === "complete" ? (
                  <Link href={step.href} onClick={step.onClick} className="group">
                    <span
                      className="absolute left-0 top-0 h-full w-1 bg-mainLightPurple lmd:bottom-0 lmd:top-auto lmd:h-1 lmd:w-full"
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lmd:pl-9" : "",
                        "flex items-start md:px-6 px-3 md:py-3 py-1.5 md:text-sm text-xs font-medium"
                      )}
                    >
                      <span className="flex-shrink-0">
                        <span className="flex h-8 w-8 mt-1 items-center justify-center rounded-full bg-mainLightPurple ">
                          <CheckIcon
                            className="h-6 w-6 text-white"
                            aria-hidden="true"
                          />
                        </span>
                      </span>
                      <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium">{step.name}</span>
                        <span className="text-sm font-medium text-gray-500">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </Link>
                ) : step.status === "current" ? (
                  <Link href={step.href} onClick={step.onClick} aria-current="step">
                    <span
                      className="absolute left-0 top-0 h-full w-1 bg-mainLightPurple lmd:bottom-0 lmd:top-auto lmd:h-1 lmd:w-full"
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lmd:pl-9" : "",
                        "flex items-start md:px-6 px-3 md:py-3 py-1.5 md:text-sm text-xs font-medium"
                      )}
                    >
                      <span className="flex-shrink-0">
                        <span className="flex h-8 w-8 mt-1 items-center justify-center rounded-full border-2 border-mainLightPurple">
                          <span className="text-mainLightPurple">{step.id}</span>
                        </span>
                      </span>
                      <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-mainLightPurple">
                          {step.name}
                        </span>
                        <span className="text-sm font-medium text-gray-500">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </Link>
                ) : (
                  <Link onClick={step.onClick} href={step.id === "2" && !templateID ? "#" : step.href} className="group">
                    <span
                      className="absolute left-0 top-0 h-full w-1 bg-transparent group-hover:bg-gray-200 lmd:bottom-0 lmd:top-auto lmd:h-1 lmd:w-full"
                      aria-hidden="true"
                    />
                    <span
                      className={classNames(
                        stepIdx !== 0 ? "lmd:pl-9" : "",
                        "flex items-start md:px-6 px-3 md:py-3 py-1.5 md:text-sm text-xs font-medium"
                      )}
                    >
                      <span className="flex-shrink-0">
                        <span className="flex h-8 w-8 mt-1 items-center justify-center rounded-full border-2 border-gray-200">
                          <span className="text-gray-500">{step.id}</span>
                        </span>
                      </span>
                      <span className="ml-4 flex min-w-0 flex-col">
                        <span className="text-sm font-medium text-gray-500">
                          {step.name}
                        </span>
                        <span className="text-sm font-medium text-gray-500">
                          {step.description}
                        </span>
                      </span>
                    </span>
                  </Link>
                )}

              </div>
            </li>
          ))}
        </ol>
      </nav>
    </div>
  );
};
